import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import axiosClient from '../../api/axiosClient'
import Button from '../Button/Button'
import InputText from '../Input/InputText'
import Typography from '../Typography'
import { theme } from '../../theme'
import { formatEuros } from '../../pages/AdminInvoices/format'

/**
 * Modal for emitting a rectificativa from an existing invoice.
 *
 * Props:
 *   - originalInvoice (required): the invoice being rectified.
 *   - alertId (optional): if the rectificativa is being emitted in response to
 *     an alert, passing the id closes the alert as `resolved` server-side.
 *   - prefill (optional): { rectificationType, rectificationReason, lines }
 *     used when the modal is opened from an alert and we already know the
 *     refund delta.
 *   - onClose (required): close the modal.
 *   - onSuccess (required): called with the new rectificativa (`{ _id, invoiceNumber, ... }`).
 *
 * The rectificativa can have:
 *   - `substitution`: full replacement of lines, non-negative amounts only.
 *   - `difference`: delta vs. original, amounts typically negative.
 */
const RectifyInvoiceModal = ({ originalInvoice, alertId, prefill, onClose, onSuccess }) => {
  const defaultLines = useMemo(() => {
    if (prefill?.lines) return prefill.lines
    // Pre-populate with original lines so admin only edits the deltas.
    return (originalInvoice?.lines || []).map((line) => ({
      paymentId: line.paymentId?.toString?.() || line.paymentId || '',
      description: line.description || '',
      taxableBase: 0,
      vatRate: line.vatRate || 0,
      vatAmount: 0
    }))
  }, [originalInvoice, prefill])

  const [rectificationType, setRectificationType] = useState(prefill?.rectificationType || 'difference')
  const [rectificationReason, setRectificationReason] = useState(prefill?.rectificationReason || '')
  const [lines, setLines] = useState(defaultLines)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const updateLine = (index, field, value) => {
    const next = [...lines]
    next[index] = { ...next[index], [field]: field === 'paymentId' || field === 'description' ? value : Number(value) }
    setLines(next)
  }

  const addLine = () => {
    setLines([...lines, { paymentId: '', description: '', taxableBase: 0, vatRate: 0, vatAmount: 0 }])
  }

  const removeLine = (index) => {
    setLines(lines.filter((_, i) => i !== index))
  }

  const totals = useMemo(() => {
    const subtotal = lines.reduce((acc, l) => acc + (Number(l.taxableBase) || 0), 0)
    const vat = lines.reduce((acc, l) => acc + (Number(l.vatAmount) || 0), 0)
    return { subtotal, vat, total: subtotal + vat }
  }, [lines])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!rectificationReason || rectificationReason.trim().length < 3) {
      setError('Indica el motivo de la rectificación.')
      return
    }
    if (lines.length === 0) {
      setError('La rectificativa debe tener al menos una línea.')
      return
    }
    if (rectificationType === 'substitution' && lines.some((l) => Number(l.taxableBase) < 0)) {
      setError('Una rectificativa por sustitución no admite importes negativos.')
      return
    }

    setSubmitting(true)
    try {
      const { data } = await axiosClient.post(
        `/api/admin/invoices/${originalInvoice._id}/rectify`,
        {
          rectificationType,
          rectificationReason: rectificationReason.trim(),
          lines: lines.map((l) => ({
            paymentId: l.paymentId,
            description: l.description || undefined,
            taxableBase: Number(l.taxableBase),
            vatRate: Number(l.vatRate),
            vatAmount: Number(l.vatAmount)
          })),
          ...(alertId ? { alertId } : {})
        }
      )
      toast.success('Rectificativa emitida.')
      onSuccess?.(data.data)
    } catch (err) {
      console.error('[RectifyInvoiceModal] rectify failed', err)
      const apiError = err?.response?.data
      setError(apiError?.message || apiError?.error || 'No se pudo emitir la rectificativa.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()}>
        <Header>
          <Typography as="h2" $variant="title-5-semibold">Emitir rectificativa</Typography>
          <CloseButton onClick={onClose} aria-label="Cerrar">×</CloseButton>
        </Header>
        <Sub>
          Sobre la factura <strong>{originalInvoice?.invoiceNumber}</strong>
          {' · '}Total original: {formatEuros(originalInvoice?.total)}
        </Sub>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>Tipo de rectificación</Label>
            <RadioGroup>
              <RadioOption>
                <input
                  type="radio"
                  name="rectType"
                  value="difference"
                  checked={rectificationType === 'difference'}
                  onChange={() => setRectificationType('difference')}
                />
                <div>
                  <strong>Por diferencias</strong>
                  <small>Se emite solo el delta (típicamente negativo por refund).</small>
                </div>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  name="rectType"
                  value="substitution"
                  checked={rectificationType === 'substitution'}
                  onChange={() => setRectificationType('substitution')}
                />
                <div>
                  <strong>Por sustitución</strong>
                  <small>Reemplaza completamente la factura original (importes no negativos).</small>
                </div>
              </RadioOption>
            </RadioGroup>
          </Field>

          <Field>
            <Label htmlFor="rectReason">Motivo</Label>
            <InputText
              id="rectReason"
              type="text"
              value={rectificationReason}
              onChange={(e) => setRectificationReason(e.target.value)}
              placeholder="Refund parcial Stripe re_xxx, error de cálculo, etc."
              $size="large"
              required
            />
          </Field>

          <Field>
            <LinesHeader>
              <Label>Líneas</Label>
              <Button type="button" size="sm" $variant="outline" onClick={addLine}>+ Añadir línea</Button>
            </LinesHeader>

            {rectificationType === 'difference' && (
              <Warning>
                Las cantidades suelen ser <strong>negativas</strong> (representan el refund/ajuste aplicado a la original).
              </Warning>
            )}

            {lines.map((line, index) => (
              <LineRow key={index}>
                <SmallField>
                  <SmallLabel>PaymentId</SmallLabel>
                  <InputText
                    type="text"
                    value={line.paymentId}
                    onChange={(e) => updateLine(index, 'paymentId', e.target.value)}
                    placeholder="ObjectId"
                  />
                </SmallField>
                <SmallField>
                  <SmallLabel>Base</SmallLabel>
                  <InputText
                    type="number"
                    step="0.01"
                    value={line.taxableBase}
                    onChange={(e) => updateLine(index, 'taxableBase', e.target.value)}
                  />
                </SmallField>
                <SmallField>
                  <SmallLabel>% IVA</SmallLabel>
                  <InputText
                    type="number"
                    step="0.01"
                    value={line.vatRate}
                    onChange={(e) => updateLine(index, 'vatRate', e.target.value)}
                  />
                </SmallField>
                <SmallField>
                  <SmallLabel>IVA €</SmallLabel>
                  <InputText
                    type="number"
                    step="0.01"
                    value={line.vatAmount}
                    onChange={(e) => updateLine(index, 'vatAmount', e.target.value)}
                  />
                </SmallField>
                <RemoveButton type="button" onClick={() => removeLine(index)} aria-label="Eliminar línea">×</RemoveButton>
              </LineRow>
            ))}
          </Field>

          <Totals>
            <span>Subtotal: <strong>{formatEuros(totals.subtotal)}</strong></span>
            <span>IVA: <strong>{formatEuros(totals.vat)}</strong></span>
            <span>Total: <strong>{formatEuros(totals.total)}</strong></span>
          </Totals>

          {error && <ErrorBox>{error}</ErrorBox>}

          <Actions>
            <Button type="button" $variant="outline" size="md" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" size="md" disabled={submitting}>
              {submitting ? 'Emitiendo…' : 'Emitir rectificativa'}
            </Button>
          </Actions>
        </Form>
      </Content>
    </Overlay>
  )
}

export default RectifyInvoiceModal

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 50%);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px;
  z-index: 1000;
  overflow-y: auto;
`

const Content = styled.div`
  background: ${theme.fill.defaultMain};
  border-radius: ${theme.radius.lg};
  width: 100%;
  max-width: 720px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 12px 32px rgb(0 0 0 / 20%);
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  color: ${theme.colors.defaultWeak};
`

const Sub = styled.div`
  font-size: 13px;
  color: ${theme.colors.defaultWeak};
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.defaultStrong};
`

const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
  }
`

const RadioOption = styled.label`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex: 1;
  padding: 12px;
  border: 1px solid ${theme.border.defaultMain};
  border-radius: ${theme.radius.md};
  cursor: pointer;
  small {
    display: block;
    font-size: 12px;
    color: ${theme.colors.defaultWeak};
    margin-top: 4px;
  }
`

const LinesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Warning = styled.div`
  padding: 8px 12px;
  background-color: #fff3cd;
  color: #856404;
  border-radius: ${theme.radius.sm};
  font-size: 12px;
`

const LineRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 0.7fr 1fr 32px;
  gap: 8px;
  align-items: end;
`

const SmallField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SmallLabel = styled.span`
  font-size: 11px;
  color: ${theme.colors.defaultWeak};
`

const RemoveButton = styled.button`
  height: 40px;
  background: none;
  border: 1px solid ${theme.border.defaultMain};
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  font-size: 18px;
  color: ${theme.colors.defaultWeak};
  &:hover { color: ${theme.colors.errorMain}; border-color: ${theme.colors.errorMain}; }
`

const Totals = styled.div`
  display: flex;
  gap: 24px;
  font-size: 13px;
  padding: 12px;
  background-color: ${theme.fill.defaultWeak || '#fafafa'};
  border-radius: ${theme.radius.md};
`

const ErrorBox = styled.div`
  padding: 10px 12px;
  background-color: #f9d6d6;
  color: #b71c1c;
  border-radius: ${theme.radius.sm};
  font-size: 13px;
`

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`
